/**
 * Kontoauszug abgleichen: reine Hilfen der Seite (Zeilen der Datenbank → Eingaben der Fachlogik, Import-Nutzlast,
 * mögliche Dubletten aus anderen Dateien, Beispielauszug für den Demomodus, Beschriftungen). Getestet in
 * test/rechnungen-kontoauszug.test.ts.
 */
import {
  addDays, buildCamt053, fingerprintTransactions, isReturnTransaction, isReturnedCredit, makeIban, maskIban, normalizeIban, openCents,
  type BankStatement, type BankTransaction, type MatchInvoice, type MatchLine, type MatchParty, type SuggestionLevel,
} from "@talentkontor/domain";
import type { InvoiceRow } from "../../../data/queries";
import { t } from "../../../lib/sprache";

export interface BankLineRow {
  id: string;
  org_id: string;
  fingerprint: string;
  source: "camt053" | "camt052" | "camt054" | "csv";
  file_name: string | null;
  own_account_masked: string | null;
  bank_ref: string | null;
  booking_date: string;
  value_date: string | null;
  amount_cents: number;
  currency: string;
  counterparty_name: string | null;
  counterparty_iban_masked: string | null;
  counterparty_iban_hash: string | null;
  remittance: string | null;
  creditor_ref: string | null;
  status: "open" | "matched" | "ignored";
  status_reason: string | null;
  related_invoice_id: string | null;
  imported_at: string;
  decided_at: string | null;
  decided_by: string | null;
  anonymized_at: string | null;
}

export interface PayerAccountRow {
  id: string;
  brand_id: string;
  iban_hash: string;
  iban_masked: string;
  holder_name: string | null;
  source: "learned" | "manual";
  created_at: string;
}

export interface LinkedPaymentRow {
  id: string;
  invoice_id: string;
  bank_line_id: string;
  kind: string;
  amount_cents: number;
  currency: string;
}

/**
 * Nutzlast je Buchung für bank_lines_import. Die IBAN geht nur flüchtig mit: Maske und Prüfwert (HMAC mit dem geheimen
 * Schlüssel der Agentur) rechnet die Datenbank, gespeichert wird die IBAN nie. Die Maske hier dient nur der Vorschau.
 */
export interface ImportLine {
  fingerprint: string;
  bank_ref: string | null;
  booking_date: string;
  value_date: string | null;
  amount_cents: number;
  currency: string;
  counterparty_name: string | null;
  counterparty_iban_masked: string | null;
  counterparty_iban: string | null;
  remittance: string;
  creditor_ref: string | null;
}

export interface ImportPlan {
  /** Neue Eingänge (Haben, gebucht) */
  lines: ImportLine[];
  /** Eingänge, die einem vorhandenen aus einer anderen Datei gleichen (Tag, Betrag, Währung, Konto) */
  maybeDuplicates: ImportLine[];
  /** Soll-Buchungen: nur zur Anzeige, werden nicht gespeichert */
  debits: BankTransaction[];
  /** Rückläufer auf der Haben-Seite (z. B. zurückgekommene Creator-Auszahlung): nur zur Anzeige, keine Zahlung auf eine Rechnung */
  returns: BankTransaction[];
  /** Vorgemerkte Buchungen (noch nicht gebucht): nicht übernommen */
  pending: number;
  /** Eingänge in einer Währung, die CreatorOffice nicht führt */
  otherCurrency: number;
  /** Davon schon früher importiert (gleicher Fingerabdruck; die Datenbank überspringt sie) */
  known: number;
  /** Buchungstag außerhalb des möglichen Zeitraums (mehr als 3 Tage in der Zukunft oder älter als rund 10 Jahre): nicht
   *  übernommen. Vorher scheiterte daran erst der ganze Import am Server (Praxistest K-142). */
  outOfRange: BankTransaction[];
  account: string | null;
}

const CURRENCIES = ["EUR", "CHF", "USD", "GBP", "SEK", "DKK"];

/** Welche Buchungen übernommen werden und was nur angezeigt wird. */
export function planImport(statement: BankStatement, orgId: string, existing: readonly Pick<BankLineRow, "fingerprint" | "booking_date" | "amount_cents" | "currency" | "counterparty_iban_masked">[], today?: string): ImportPlan {
  // dieselbe Grenze wie public.bank_import (0070): heute + 3 Tage bis heute − 3660 Tage
  const tag = (iso: string, plus: number) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + plus); return d.toISOString().slice(0, 10); };
  const spaetestens = today ? tag(today, 3) : null;
  const fruehestens = today ? tag(today, -3660) : null;
  const fps = fingerprintTransactions(statement.transactions, orgId, statement.accountIban);
  const known = new Set(existing.map((e) => e.fingerprint));
  const plan: ImportPlan = { lines: [], maybeDuplicates: [], debits: [], returns: [], pending: 0, otherCurrency: 0, known: 0, outOfRange: [], account: maskIban(statement.accountIban) };
  statement.transactions.forEach((t, k) => {
    if (t.pending) {
      plan.pending++;
      return;
    }
    if (t.direction === "debit") {
      plan.debits.push(t);
      return;
    }
    if (isReturnedCredit(t)) {
      plan.returns.push(t);
      return;
    }
    if (!CURRENCIES.includes(t.currency)) {
      plan.otherCurrency++;
      return;
    }
    if (spaetestens && fruehestens && (t.bookingDate > spaetestens || t.bookingDate < fruehestens)) {
      plan.outOfRange.push(t);
      return;
    }
    const line: ImportLine = {
      fingerprint: fps[k]!,
      bank_ref: t.bankRef,
      booking_date: t.bookingDate,
      value_date: t.valueDate,
      amount_cents: t.amountCents,
      currency: t.currency,
      counterparty_name: t.counterpartyName,
      counterparty_iban_masked: maskIban(t.counterpartyIban),
      counterparty_iban: t.counterpartyIban ? t.counterpartyIban.replace(/\s+/g, "").slice(0, 64) || null : null,
      remittance: [t.remittance, ...t.referredDocs.map((d) => `Beleg ${d}`)].filter(Boolean).join(" ").slice(0, 500),
      creditor_ref: t.creditorRef ? t.creditorRef.slice(0, 40) : null,
    };
    // Gleicher Tag, Betrag, Währung und (falls bekannt) dasselbe Konto, aber anderer Fingerabdruck: vermutlich derselbe
    // Eingang aus einer anderen Datei (z. B. erst CSV, dann CAMT)
    const similar = !known.has(line.fingerprint) && existing.some((e) => e.booking_date === line.booking_date && e.amount_cents === line.amount_cents
      && e.currency === line.currency && (!e.counterparty_iban_masked || !line.counterparty_iban_masked || e.counterparty_iban_masked === line.counterparty_iban_masked));
    if (known.has(line.fingerprint)) plan.known++;
    (similar ? plan.maybeDuplicates : plan.lines).push(line);
  });
  return plan;
}

/** Rechnungen der Agentur als Eingabe der Vorschläge. */
export function toMatchInvoices(invoices: readonly InvoiceRow[]): MatchInvoice[] {
  return invoices.map((i) => ({
    id: i.id, number: i.number, kind: i.kind, status: i.status, currency: i.currency, grossCents: i.gross_cents ?? 0, paidCents: Number(i.paid_cents ?? 0),
    brandId: i.brand_id, talentId: i.talent_id, dueDate: i.effective_due_date ?? i.document_due_date, issueDate: i.issue_date, creditFor: i.credit_for ?? null,
    paymentRef: ((i as { issuer_snapshot?: { bank?: { qr?: { ref?: unknown } } } | null }).issuer_snapshot?.bank?.qr?.ref as string | undefined) ?? null,
  }));
}

export interface PartySource {
  brands: readonly { id: string; name: string; legal_name?: string | null }[];
  talents: readonly { id: string; first_name: string; last_name?: string | null; display_name?: string | null; company_name?: string | null }[];
  payerAccounts: readonly Pick<PayerAccountRow, "brand_id" | "iban_hash">[];
  /** Prüfwerte der Auszahlungskonten der Creator (von der Datenbank gerechnet, bank_talent_iban_hashes) */
  talentHashes: readonly { talent_id: string; iban_hash: string | null }[];
}

/** Marken und Creator als mögliche Zahler mit Namen und Prüfwerten bekannter Konten. */
export function toParties(src: PartySource): MatchParty[] {
  const brandHashes = new Map<string, string[]>();
  for (const a of src.payerAccounts) brandHashes.set(a.brand_id, [...(brandHashes.get(a.brand_id) ?? []), a.iban_hash]);
  const talentHashes = new Map<string, string[]>();
  for (const t of src.talentHashes) {
    if (t.iban_hash) talentHashes.set(t.talent_id, [...(talentHashes.get(t.talent_id) ?? []), t.iban_hash]);
  }
  const uniq = (xs: (string | null | undefined)[]) => [...new Set(xs.map((x) => (x ?? "").trim()).filter(Boolean))];
  return [
    ...src.brands.map((b) => ({ id: b.id, kind: "brand" as const, names: uniq([b.name, b.legal_name]), ibanHashes: brandHashes.get(b.id) ?? [] })),
    ...src.talents.map((t) => ({ id: t.id, kind: "talent" as const, names: uniq([`${t.first_name} ${t.last_name ?? ""}`, t.display_name, t.company_name]), ibanHashes: talentHashes.get(t.id) ?? [] })),
  ];
}

export function toMatchLine(l: BankLineRow): MatchLine {
  return { direction: "credit", amountCents: l.amount_cents, currency: l.currency, counterpartyName: l.counterparty_name, counterpartyIbanHash: l.counterparty_iban_hash,
    remittance: l.remittance, creditorRef: l.creditor_ref };
}

export const LEVEL_PILL: Record<SuggestionLevel, { tone: string; symbol: string; label: string }> = {
  sicher: { tone: "success", symbol: "✓", get label() { return t("Sicher", "Certain"); } },
  wahrscheinlich: { tone: "info", symbol: "◇", get label() { return t("Wahrscheinlich", "Likely"); } },
  möglich: { tone: "warning", symbol: "◷", get label() { return t("Möglich", "Possible"); } },
  hinweis: { tone: "warning", symbol: "!", get label() { return t("Hinweis", "Note"); } },
};
export const STATUS_PILL: Record<BankLineRow["status"], { tone: string; symbol: string; label: string }> = {
  open: { tone: "neutral", symbol: "○", get label() { return t("Offen", "Open"); } },
  matched: { tone: "success", symbol: "✓", get label() { return t("Zugeordnet", "Matched"); } },
  ignored: { tone: "neutral", symbol: "○", get label() { return t("Ignoriert", "Ignored"); } },
};

/** Vorlagen für den Grund beim Ignorieren */
export const IGNORE_REASONS = (): string[] => [
  t("Keine Rechnung (z. B. Erstattung, Zinsen, Umbuchung)", "No invoice (e.g. refund, interest, transfer)"),
  t("Creator-Rechnung im Direktweg: an den Creator weitergeleitet", "Creator invoice on the direct route: forwarded to the creator"),
  t("Doppelzahlung: Erstattung an die Marke veranlasst", "Double payment: refund to the brand arranged"),
  t("Marke zahlte an den Creator: an der Rechnung vermerkt", "Brand paid the creator: noted on the invoice"),
  t("Schon von Hand an der Rechnung erfasst", "Already recorded by hand on the invoice"),
];

/** Abgang als Rücklastschrift, und welche zugeordneten Eingänge dazu passen könnten (gleicher Betrag, gleiches Konto laut Maske). */
export function returnCandidates(debit: BankTransaction, matched: readonly BankLineRow[]): BankLineRow[] {
  if (!isReturnTransaction(debit)) return [];
  const mask = maskIban(debit.counterpartyIban);
  return matched.filter((l) => l.status === "matched" && l.amount_cents === debit.amountCents && l.currency === debit.currency
    && (!mask || !l.counterparty_iban_masked || l.counterparty_iban_masked === mask));
}

// ── Beispielauszug für den Demomodus ─────────────────────────────────────────

export interface DemoSource {
  orgName: string;
  currency: string;
  country: string | null;
  today: string;
  invoices: readonly InvoiceRow[];
  brands: readonly { id: string; name: string; legal_name?: string | null }[];
}

const pad = (n: number, len: number) => String(n).padStart(len, "0");

/**
 * Erfundener Tagesauszug (camt.053) passend zu den offenen Rechnungen der Agentur: ein Eingang genau über eine Rechnung
 * (Nummer ohne Bindestriche), eine Sammelzahlung einer Marke über zwei Rechnungen ohne Nummer, eine Teilzahlung, ein
 * Eingang ohne Treffer, ein Rückläufer (zurückgekommene Auszahlung) und eine Rücklastschrift. Fehlen passende
 * Rechnungen, entfallen die jeweiligen Buchungen.
 */
export function demoStatementXml(src: DemoSource): { xml: string; fileName: string } {
  const open = src.invoices
    .filter((i) => i.kind === "agency_to_brand" && i.status === "sent" && !i.credit_for && i.currency === src.currency && (i.gross_cents ?? 0) - Number(i.paid_cents ?? 0) > 0)
    .sort((a, b) => String(a.effective_due_date ?? "").localeCompare(String(b.effective_due_date ?? "")) || String(a.number).localeCompare(String(b.number)));
  const brand = (id: string | null) => src.brands.find((b) => b.id === id);
  const payer = (id: string | null) => {
    const b = brand(id);
    return b?.legal_name?.trim() || (b ? `${b.name} GmbH` : t("Unbekannte Marke", "Unknown brand"));
  };
  const country = src.currency === "CHF" ? "CH" : src.country === "AT" ? "AT" : "DE";
  const iban = (n: number) => makeIban(country, country === "CH" ? `00762${pad(n, 12)}` : country === "AT" ? `19043${pad(n, 11)}` : `12030000${pad(n, 10)}`);
  const used = new Set<string>();
  const entries: Parameters<typeof buildCamt053>[0]["entries"][number][] = [];
  const day = (k: number) => addDays(src.today, -k);
  let ref = 0;
  const nextRef = () => `DEMO${src.today.replace(/-/g, "")}${pad(++ref, 4)}`;
  // Sammelzahlung: Marke mit mindestens zwei offenen Rechnungen
  const byBrand = new Map<string, InvoiceRow[]>();
  for (const i of open) if (i.brand_id) byBrand.set(i.brand_id, [...(byBrand.get(i.brand_id) ?? []), i]);
  const group = [...byBrand.values()].find((xs) => xs.length >= 2);
  if (group) {
    const [a, b] = group;
    for (const x of [a!, b!]) used.add(x.id);
    entries.push({ ref: nextRef(), bookingDate: day(1), direction: "credit", amountCents: openCents({ grossCents: a!.gross_cents ?? 0, paidCents: Number(a!.paid_cents ?? 0) }) + openCents({ grossCents: b!.gross_cents ?? 0, paidCents: Number(b!.paid_cents ?? 0) }),
      counterpartyName: payer(a!.brand_id), counterpartyIban: iban(1), remittance: "Sammelueberweisung offene Rechnungen" });
  }
  // Genau eine Rechnung, Nummer ohne Bindestriche
  const exact = open.find((i) => !used.has(i.id));
  if (exact) {
    used.add(exact.id);
    entries.push({ ref: nextRef(), bookingDate: day(2), direction: "credit", amountCents: (exact.gross_cents ?? 0) - Number(exact.paid_cents ?? 0),
      counterpartyName: payer(exact.brand_id).toUpperCase(), counterpartyIban: iban(2), remittance: `RE ${String(exact.number ?? "").replace(/[^0-9A-Za-z]/g, "")} DANKE` });
  }
  // Teilzahlung mit Nummer
  const part = open.find((i) => !used.has(i.id));
  if (part) {
    used.add(part.id);
    const openAmount = (part.gross_cents ?? 0) - Number(part.paid_cents ?? 0);
    entries.push({ ref: nextRef(), bookingDate: day(1), direction: "credit", amountCents: Math.max(100, Math.round(openAmount * 0.6 / 100) * 100),
      counterpartyName: payer(part.brand_id), counterpartyIban: iban(3), remittance: `Teilzahlung Rechnung ${part.number ?? ""}` });
  }
  // Ohne Treffer, ein Rückläufer und eine Rücklastschrift (die beiden letzten nur Anzeige)
  entries.push({ ref: nextRef(), bookingDate: day(0), direction: "credit", amountCents: 24990, counterpartyName: "Muster Versand GmbH", counterpartyIban: iban(4), remittance: "Erstattung Retoure 4711" });
  entries.push({ ref: nextRef(), bookingDate: day(0), direction: "credit", amountCents: 85000, counterpartyName: "Beispiel Empfänger", counterpartyIban: iban(6), remittance: "Rueckueberweisung Auszahlung, Konto erloschen", reversal: true });
  entries.push({ ref: nextRef(), bookingDate: day(0), direction: "debit", amountCents: 1990, counterpartyName: "Beispiel Software AG", counterpartyIban: iban(5), remittance: "Ruecklastschrift Lizenz September", reversal: true });
  const own = makeIban(country, country === "CH" ? `00762${pad(999, 12)}` : country === "AT" ? `19043${pad(999, 11)}` : `37040044${pad(999, 10)}`);
  const xml = buildCamt053({
    messageId: `DEMO-${src.today}`, createdAt: `${src.today}T07:00:00`, accountIban: own, accountOwner: src.orgName, currency: src.currency,
    statementId: `DEMO-${src.today}`, from: day(3), to: src.today, openingBalanceCents: 1250000, entries,
  });
  return { xml, fileName: `beispielauszug-${src.today}.camt053.xml` };
}

/** Prüft eine eingegebene IBAN für „Konto hinterlegen“; Maske und Prüfwert rechnet die Datenbank, gespeichert wird nur das. */
export function payerAccountInput(input: string): { iban: string; masked: string } | null {
  const n = normalizeIban(input);
  if (!n) return null;
  return { iban: n, masked: maskIban(n)! };
}

/** Datei lesen: UTF-8, sonst Windows-1252 (ältere Bankexporte mit Umlauten). */
export function decodeStatementFile(bytes: Uint8Array): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
}
